import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Mail, Smartphone, RefreshCw, BellRing } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { projectTypeLabels } from "./labels";

export const KIND_LABELS = {
  new_lead: { label: "Nouveau lead", class: "bg-blue-100 text-blue-700" },
  unsold_48h: { label: "Rappel 48h", class: "bg-amber-100 text-amber-700" },
  unsold_7d: { label: "Rappel J+7", class: "bg-red-100 text-red-700" },
  daily_recap: { label: "Résumé quotidien", class: "bg-slate-100 text-slate-700" },
  purchase: { label: "Achat confirmé", class: "bg-green-100 text-green-700" },
  daily: { label: "Résumé quotidien", class: "bg-slate-100 text-slate-700" },
  lead: { label: "Nouveau lead", class: "bg-blue-100 text-blue-700" },
  unsold: { label: "Rappel", class: "bg-amber-100 text-amber-700" },
  admin: { label: "Admin", class: "bg-slate-100 text-slate-700" },
  test: { label: "Test", class: "bg-slate-100 text-slate-500" },
};

export const kindBadge = (kind) => {
  const k = KIND_LABELS[kind] || KIND_LABELS[(kind || "").split("-")[0]] || { label: kind, class: "bg-slate-100 text-slate-600" };
  return <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${k.class}`}>{k.label}</span>;
};

export const fmtDateTime = (d) => (d ? new Date(d).toLocaleString("fr-BE", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");

export const NotificationsLogTab = ({ API }) => {
  const [rows, setRows] = useState(null);
  const [audience, setAudience] = useState("");
  const [kind, setKind] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/notifications-log`, { params: { audience, kind }, withCredentials: true });
      setRows(r.data);
    } catch { setRows([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [audience, kind]); // eslint-disable-line react-hooks/exhaustive-deps

  const Pill = ({ active, onClick, children, testId }) => (
    <button type="button" onClick={onClick} data-testid={testId}
      className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
      {children}
    </button>
  );

  return (
    <div className="space-y-4" data-testid="notifications-log-tab">
      <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap items-center gap-2">
        <BellRing className="w-4 h-4 text-slate-400" />
        <span className="text-sm font-medium text-slate-700 mr-2">Journal des notifications</span>
        <Pill active={audience === ""} onClick={() => setAudience("")} testId="log-aud-all">Tous</Pill>
        <Pill active={audience === "contractor"} onClick={() => setAudience("contractor")} testId="log-aud-contractor">Entrepreneurs</Pill>
        <Pill active={audience === "admin"} onClick={() => setAudience("admin")} testId="log-aud-admin">Admin</Pill>
        <span className="mx-1 text-slate-200">|</span>
        <Pill active={kind === ""} onClick={() => setKind("")} testId="log-kind-all">Tous types</Pill>
        <Pill active={kind === "new_lead"} onClick={() => setKind("new_lead")} testId="log-kind-new">Nouveaux leads</Pill>
        <Pill active={kind === "unsold_48h"} onClick={() => setKind("unsold_48h")} testId="log-kind-48h">Rappels 48h</Pill>
        <Pill active={kind === "unsold_7d"} onClick={() => setKind("unsold_7d")} testId="log-kind-7d">Rappels J+7</Pill>
        <Button variant="outline" size="sm" onClick={load} className="ml-auto" data-testid="log-refresh"><RefreshCw className="w-4 h-4 mr-1" />Actualiser</Button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading || rows === null ? (
          <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-orange-500" /></div>
        ) : rows.length === 0 ? (
          <div className="py-16 text-center text-slate-500" data-testid="log-empty">Aucune notification enregistrée pour le moment.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Canal</TableHead><TableHead>Destinataire</TableHead><TableHead>Lead</TableHead><TableHead>Sujet</TableHead><TableHead>État</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={i} data-testid={`log-row-${i}`}>
                  <TableCell className="text-sm text-slate-500 whitespace-nowrap">{fmtDateTime(r.created_at)}</TableCell>
                  <TableCell>{kindBadge(r.kind)}</TableCell>
                  <TableCell>{r.channel === "push" ? <span className="inline-flex items-center gap-1 text-xs text-slate-600"><Smartphone className="w-3 h-3" />Push</span> : <span className="inline-flex items-center gap-1 text-xs text-slate-600"><Mail className="w-3 h-3" />E-mail</span>}</TableCell>
                  <TableCell className="text-sm"><p className="text-slate-900">{r.company_name || (r.audience === "admin" ? "Admin" : "")}</p><p className="text-xs text-slate-500 truncate max-w-[220px]">{r.recipient}</p></TableCell>
                  <TableCell className="text-sm">{r.lead_number ? <span className="font-mono text-xs text-orange-600 font-bold">{r.lead_number}</span> : <span className="text-slate-400">—</span>}{r.project_type && <p className="text-xs text-slate-500">{projectTypeLabels[r.project_type] || r.project_type}{r.city ? ` · ${r.city}` : ""}</p>}</TableCell>
                  <TableCell className="text-sm text-slate-700 max-w-[320px] truncate" title={r.subject}>{r.subject}</TableCell>
                  <TableCell>{r.ok === false ? <span className="text-xs font-semibold text-red-600">Échec</span> : <span className="text-xs font-semibold text-green-600">Envoyé</span>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
};
